import { useEffect } from 'react';

// Scroll the app's main content area (and the window, as a fallback) back to the top.
//
// App.js already resets the main scroller when you navigate to a different MODULE (sidebar).
// Call this helper for IN-PAGE transitions that don't change the module — switching a primary
// tab/sub-view, advancing a wizard step, or opening a list item's detail view — so the new
// content doesn't inherit the previous view's scroll position (a common "stuck at the bottom"
// glitch).
//
// For a component that has its OWN inner scroll container (e.g. a modal body or a panel with
// `overflow-y-auto`), use `useScrollToTopOnChange` below — this helper only knows about the
// shared app shell + window.
export const scrollContentToTop = (behavior = 'auto') => {
  try {
    const main = document.getElementById('app-main');
    if (main) main.scrollTo({ top: 0, behavior });
    window.scrollTo({ top: 0, behavior });
  } catch {
    /* no-op (SSR / detached DOM) */
  }
};

/**
 * Reset a component's own scroll container to the top whenever `deps` change.
 *
 * A paged list inside a fixed-height panel keeps the panel's scroll position across a page change,
 * because only the rows are replaced and the scroller itself never moves. Reading the second page
 * then starts you at the bottom of it, looking at the last row. The same applies to any change
 * that swaps the visible rows: a search term, a filter, a different page size.
 *
 * Pass the ref attached to the element carrying `overflow-y-auto`, and every piece of state that
 * changes which rows are shown. Jumps instantly rather than smooth-scrolling: the content under
 * the cursor has already been replaced, so animating a scroll through rows nobody asked to see
 * just delays the list they did.
 */
export const useScrollToTopOnChange = (ref, deps) => {
  useEffect(() => {
    const el = ref?.current;
    if (el) el.scrollTop = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
